import React, { useEffect, useState, useRef, Component } from 'react';
import { ShoppingBagIcon, StoreIcon, GlobeIcon, PlusIcon, EyeIcon, QrCodeIcon, LinkIcon, PackageIcon, DollarSignIcon, ChevronRightIcon, MoreHorizontalIcon, CreditCardIcon, TrashIcon, EyeOffIcon, CheckIcon, XIcon, CopyIcon, Share2Icon, EditIcon } from 'lucide-react';
// Shop type definition
export interface Shop {
  id: string;
  name: string;
  description: string;
  logo: string;
  color: string;
  isOpen: boolean;
  channels: ('pos' | 'ecommerce')[];
  productsCount: number;
  revenue: {
    total: number;
    currency: string;
    growth: number;
  };
  ordersCount: number;
}
export function Shops() {
  // Mock shops data
  const [shops, setShops] = useState<Shop[]>([{
    id: '1',
    name: 'Beach Shop',
    description: 'Beachwear and accessories for summer holidays',
    logo: '•',
    color: '#f8d7d7',
    isOpen: true,
    channels: ['pos', 'ecommerce'],
    productsCount: 48,
    revenue: {
      total: 12580,
      currency: 'USD',
      growth: 15.3
    },
    ordersCount: 124
  }, {
    id: '2',
    name: 'Island Shop',
    description: 'Local island products and souvenirs',
    logo: '+',
    color: '#e1f5fe',
    isOpen: true,
    channels: ['ecommerce'],
    productsCount: 32,
    revenue: {
      total: 8750,
      currency: 'USD',
      growth: 8.7
    },
    ordersCount: 86
  }, {
    id: '3',
    name: 'ABC',
    description: 'Convenience store with everyday essentials',
    logo: 'A',
    color: '#e8f5e9',
    isOpen: false,
    channels: ['pos'],
    productsCount: 156,
    revenue: {
      total: 24680,
      currency: 'USD',
      growth: -2.4
    },
    ordersCount: 312
  }]);
  // State for dropdown menus and share modal
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [shareModalShopId, setShareModalShopId] = useState<string | null>(null);
  const [showQRCode, setShowQRCode] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpenDropdownId(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  // Toggle shop open/closed status
  const toggleShopStatus = (shopId: string) => {
    setShops(shops.map(shop => shop.id === shopId ? {
      ...shop,
      isOpen: !shop.isOpen
    } : shop));
    setOpenDropdownId(null);
  };
  // Navigate to shop details
  const navigateToShopDetails = (shopId: string) => {
    window.location.href = `/shop-details?id=${shopId}`;
    setOpenDropdownId(null);
  };
  // Navigate to create new shop
  const navigateToCreateShop = () => {
    window.location.href = '/shop-details?new=true';
  };
  // Navigate to edit shop
  const navigateToEditShop = (shopId: string) => {
    window.location.href = `/shop-details?id=${shopId}&edit=true`;
    setOpenDropdownId(null);
  };
  // Toggle dropdown menu
  const toggleDropdown = (shopId: string) => {
    setOpenDropdownId(openDropdownId === shopId ? null : shopId);
  };
  // Open share modal
  const openShareModal = (shopId: string) => {
    setShareModalShopId(shopId);
    setShowQRCode(false);
    setOpenDropdownId(null);
  };
  // Close share modal
  const closeShareModal = () => {
    setShareModalShopId(null);
  };
  // Copy shop URL to clipboard
  const copyShopUrl = (shopId: string) => {
    const shopUrl = `https://yourwebsite.com/shop/${shopId}`;
    navigator.clipboard.writeText(shopUrl).then(() => {
      // Show success message (could use a toast here)
      alert('Shop URL copied to clipboard!');
    }, err => {
      console.error('Could not copy text: ', err);
    });
  };
  // Delete shop
  const deleteShop = (shopId: string) => {
    if (confirm('Are you sure you want to delete this shop? This action cannot be undone.')) {
      setShops(shops.filter(shop => shop.id !== shopId));
    }
    setOpenDropdownId(null);
  };
  // Share Modal Component
  const ShareModal = ({
    shopId
  }: {
    shopId: string;
  }) => {
    const shop = shops.find(s => s.id === shopId);
    if (!shop) return null;
    return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-5792">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" data-id="element-5793">
          <div className="flex justify-between items-center mb-4" data-id="element-5794">
            <h3 className="text-lg font-medium text-gray-900" data-id="element-5795">
              Share {shop.name}
            </h3>
            <button onClick={closeShareModal} className="text-gray-400 hover:text-gray-500" data-id="element-5796">
              <XIcon size={20} data-id="element-5797" />
            </button>
          </div>
          {showQRCode ? <div className="text-center" data-id="element-5798">
              <div className="bg-white p-4 rounded-lg inline-block mb-4 border border-gray-200" data-id="element-5799">
                <div className="w-48 h-48 bg-gray-100 rounded flex items-center justify-center" data-id="element-5800">
                  <QrCodeIcon size={100} className="text-gray-400" data-id="element-5801" />
                  <span className="absolute text-xs text-gray-500" data-id="element-5802">
                    QR Code Preview
                  </span>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-4" data-id="element-5803">
                Scan this QR code to visit {shop.name}
              </p>
              <button onClick={() => setShowQRCode(false)} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-md text-gray-700 text-sm font-medium" data-id="element-5804">
                Back to share options
              </button>
            </div> : <>
              <div className="space-y-4 mb-4" data-id="element-5805">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md" data-id="element-5806">
                  <div className="flex items-center" data-id="element-5807">
                    <LinkIcon size={18} className="text-gray-500 mr-2" data-id="element-5808" />
                    <span className="text-sm text-gray-600 truncate max-w-[200px]" data-id="element-5809">
                      https://yourwebsite.com/shop/{shopId}
                    </span>
                  </div>
                  <button onClick={() => copyShopUrl(shopId)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-md" data-id="element-5810">
                    <CopyIcon size={16} data-id="element-5811" />
                  </button>
                </div>
                <button onClick={() => setShowQRCode(true)} className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-md hover:bg-gray-100" data-id="element-5812">
                  <div className="flex items-center" data-id="element-5813">
                    <QrCodeIcon size={18} className="text-gray-500 mr-2" data-id="element-5814" />
                    <span className="text-sm text-gray-600" data-id="element-5815">
                      Generate QR Code
                    </span>
                  </div>
                  <ChevronRightIcon size={16} className="text-gray-400" data-id="element-5816" />
                </button>
              </div>
              <div className="flex justify-end" data-id="element-5817">
                <button onClick={closeShareModal} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-md text-gray-700 text-sm font-medium" data-id="element-5818">
                  Close
                </button>
              </div>
            </>}
        </div>
      </div>;
  };
  return <div className="space-y-6" data-id="element-5819">
      {/* Header with title and actions */}
      <div className="flex justify-between items-center" data-id="element-5820">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center" data-id="element-5821">
          <ShoppingBagIcon size={24} className="mr-2 text-blue-600" data-id="element-5822" />
          Shops
        </h1>
        <button onClick={navigateToCreateShop} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center transition-colors" data-id="element-5823">
          <PlusIcon size={16} className="mr-2" data-id="element-5824" />
          Create Shop
        </button>
      </div>
      {/* Shops list */}
      <div className="grid grid-cols-1 gap-6" data-id="element-5825">
        {shops.map(shop => <div key={shop.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-id="element-5826">
            <div className="p-6" data-id="element-5827">
              <div className="flex items-start justify-between" data-id="element-5828">
                <div className="flex items-start space-x-4" data-id="element-5829">
                  <div className="w-12 h-12 rounded-md flex items-center justify-center flex-shrink-0" style={{
                backgroundColor: shop.color
              }} data-id="element-5830">
                    <span className="text-xl font-bold text-red-600" data-id="element-5831">
                      {shop.logo}
                    </span>
                  </div>
                  <div data-id="element-5832">
                    <h2 onClick={() => navigateToShopDetails(shop.id)} className="text-lg font-medium text-gray-900 hover:text-blue-600 cursor-pointer" data-id="element-5833">
                      {shop.name}
                    </h2>
                    <p className="text-sm text-gray-500 mt-1" data-id="element-5834">
                      {shop.description}
                    </p>
                    <div className="flex items-center mt-2 space-x-3" data-id="element-5835">
                      {/* Shop channels */}
                      <div className="flex items-center space-x-1" data-id="element-5836">
                        {shop.channels.includes('pos') && <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-800" data-id="element-5837">
                            <CreditCardIcon size={12} className="mr-1" data-id="element-5838" />
                            POS
                          </span>}
                        {shop.channels.includes('ecommerce') && <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800" data-id="element-5839">
                            <GlobeIcon size={12} className="mr-1" data-id="element-5840" />
                            E-Commerce
                          </span>}
                      </div>
                      {/* Shop status */}
                      <div className="flex items-center" data-id="element-5841">
                        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${shop.isOpen ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-5842">
                          {shop.isOpen ? 'Open' : 'Closed'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Shop actions dropdown */}
                <div className="relative" data-id="element-5843">
                  <button onClick={() => toggleDropdown(shop.id)} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md" title="More Options" data-id="element-5844">
                    <MoreHorizontalIcon size={16} data-id="element-5845" />
                  </button>
                  {openDropdownId === shop.id && <div ref={dropdownRef} className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10" data-id="element-5846">
                      <div className="py-1" role="menu" aria-orientation="vertical" data-id="element-5847">
                        {shop.isOpen ? <button onClick={() => toggleShopStatus(shop.id)} className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" role="menuitem" data-id="element-5848">
                            <EyeOffIcon size={16} className="mr-3 text-gray-500" data-id="element-5849" />
                            Close Shop
                          </button> : <button onClick={() => toggleShopStatus(shop.id)} className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" role="menuitem" data-id="element-5850">
                            <EyeIcon size={16} className="mr-3 text-gray-500" data-id="element-5851" />
                            Open Shop
                          </button>}
                        <button onClick={() => openShareModal(shop.id)} className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" role="menuitem" data-id="element-5852">
                          <Share2Icon size={16} className="mr-3 text-gray-500" data-id="element-5853" />
                          Share Shop
                        </button>
                        <button onClick={() => navigateToShopDetails(shop.id)} className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" role="menuitem" data-id="element-5854">
                          <EyeIcon size={16} className="mr-3 text-gray-500" data-id="element-5855" />
                          View Details
                        </button>
                        <button onClick={() => navigateToEditShop(shop.id)} className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" role="menuitem" data-id="element-5856">
                          <EditIcon size={16} className="mr-3 text-gray-500" data-id="element-5857" />
                          Edit Shop
                        </button>
                        <div className="border-t border-gray-100 my-1" data-id="element-5858"></div>
                        <button onClick={() => deleteShop(shop.id)} className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50" role="menuitem" data-id="element-5859">
                          <TrashIcon size={16} className="mr-3 text-red-500" data-id="element-5860" />
                          Delete Shop
                        </button>
                      </div>
                    </div>}
                </div>
              </div>
              {/* Shop statistics */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 border-t border-gray-200 pt-4" data-id="element-5861">
                <div className="flex items-center" data-id="element-5862">
                  <div className="p-2 bg-blue-50 rounded-md mr-3" data-id="element-5863">
                    <PackageIcon size={16} className="text-blue-600" data-id="element-5864" />
                  </div>
                  <div data-id="element-5865">
                    <p className="text-sm text-gray-500" data-id="element-5866">Products</p>
                    <p className="text-lg font-medium" data-id="element-5867">{shop.productsCount}</p>
                  </div>
                </div>
                <div className="flex items-center" data-id="element-5868">
                  <div className="p-2 bg-green-50 rounded-md mr-3" data-id="element-5869">
                    <DollarSignIcon size={16} className="text-green-600" data-id="element-5870" />
                  </div>
                  <div data-id="element-5871">
                    <p className="text-sm text-gray-500" data-id="element-5872">Revenue</p>
                    <div className="flex items-center" data-id="element-5873">
                      <p className="text-lg font-medium" data-id="element-5874">
                        ${shop.revenue.total.toLocaleString()}
                      </p>
                      <span className={`ml-2 text-xs ${shop.revenue.growth >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-5875">
                        {shop.revenue.growth >= 0 ? '+' : ''}
                        {shop.revenue.growth}%
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center" data-id="element-5876">
                  <div className="p-2 bg-purple-50 rounded-md mr-3" data-id="element-5877">
                    <ShoppingBagIcon size={16} className="text-purple-600" data-id="element-5878" />
                  </div>
                  <div data-id="element-5879">
                    <p className="text-sm text-gray-500" data-id="element-5880">Orders</p>
                    <p className="text-lg font-medium" data-id="element-5881">{shop.ordersCount}</p>
                  </div>
                </div>
              </div>
              {/* View Details Footer */}
              <div className="mt-4 pt-4 border-t border-gray-200 flex justify-end" data-id="element-5882">
                <button onClick={() => navigateToShopDetails(shop.id)} className="flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium" data-id="element-5883">
                  View Details
                  <ChevronRightIcon size={16} className="ml-1" data-id="element-5884" />
                </button>
              </div>
            </div>
          </div>)}
      </div>
      {/* Empty state if no shops */}
      {shops.length === 0 && <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center" data-id="element-5885">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-5886">
            <ShoppingBagIcon size={24} className="text-blue-600" data-id="element-5887" />
          </div>
          <h3 className="text-lg font-medium text-gray-900" data-id="element-5888">
            No shops created yet
          </h3>
          <p className="text-gray-500 mt-2 mb-6" data-id="element-5889">
            Create your first shop to start selling products online or in-store.
          </p>
          <button onClick={navigateToCreateShop} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md inline-flex items-center transition-colors" data-id="element-5890">
            <PlusIcon size={16} className="mr-2" data-id="element-5891" />
            Create Shop
          </button>
        </div>}
      {/* Share Modal */}
      {shareModalShopId && <ShareModal shopId={shareModalShopId} data-id="element-5892" />}
    </div>;
}